<script setup>
import IconChevron from "../icons/IconChevron.vue";

defineProps({
  question: { type: String, required: true },
  open: { type: Boolean, default: false },
  id: { type: String, required: true },
});

const emit = defineEmits(["toggle"]);
</script>

<template>
  <div class="accordion" :class="{ 'is-open': open }">
    <h3 class="accordion__heading">
      <button
        class="accordion__trigger"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="`${id}-panel`"
        :id="`${id}-trigger`"
        @click="emit('toggle')"
      >
        <span class="accordion__question">{{ question }}</span>
        <IconChevron class="accordion__icon" aria-hidden="true" />
      </button>
    </h3>

    <!-- grid-template-rows 0fr -> 1fr animates without measuring heights. -->
    <div
      class="collapse"
      :class="{ 'is-open': open }"
      :id="`${id}-panel`"
      role="region"
      :aria-labelledby="`${id}-trigger`"
    >
      <div class="collapse__inner">
        <div class="accordion__answer">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.accordion {
  border-block-end: var(--border);
  transition: border-color var(--dur-fast) var(--ease-in-out);
}

.accordion:first-child {
  border-block-start: var(--border);
}

.accordion__heading {
  margin: 0;
  font-size: inherit;
}

.accordion__trigger {
  inline-size: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding-block: var(--space-5);
  text-align: start;
  color: var(--ink);
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  transition: color var(--dur-fast) var(--ease-in-out);
}

.accordion__trigger:hover {
  color: var(--brand);
}

.accordion__icon {
  inline-size: 20px;
  block-size: 20px;
  flex-shrink: 0;
  color: var(--brand);
  transition: transform var(--dur-base) var(--ease-in-out);
}

.accordion.is-open .accordion__icon {
  transform: rotate(180deg);
}

.accordion__answer {
  padding-block-end: var(--space-5);
  padding-inline-end: var(--space-8);
  color: var(--ink-muted);
  line-height: var(--leading-relaxed);
}
</style>
